import { AlignCenter, AlignLeft, ArrowDown, ArrowUp, Eye, EyeOff, Paintbrush, Save, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  DEFAULT_SECTION_ORDER, DEFAULT_STYLES, FONT_OPTIONS, HomeSections, SECTION_LABELS, mergeStyles,
} from "@/components/storefront/HomeSections";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useSite } from "@/context/SiteContext";
import { API, errMsg } from "@/lib/api";

const SHOW_FLAG = {
  occasions: "show_categories",
  features: "show_features",
  accreditation: "show_accreditation",
  featured: "show_featured",
  promo: "show_promo",
  testimonials: "show_testimonials",
  gallery: "show_gallery",
  advert: "show_hg",
};

function ColorField({ label, value, onChange, allowClear = true, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || "#4A5D4E"}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 rounded border cursor-pointer"
          data-testid={testId}
        />
        {value ? <span className="text-xs text-muted-foreground font-mono">{value}</span> : <span className="text-xs text-muted-foreground">Default</span>}
        {allowClear && value && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onChange("")} data-testid={`${testId}-clear`}>Reset</Button>
        )}
      </div>
    </div>
  );
}

export default function AdminLandingEditor() {
  const { refresh, categoryTree } = useSite();
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState({ settings: null, occasionsChildren: [], featured: [], testimonials: [], gallery: [] });
  const [selected, setSelected] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get("/admin/content/home"),
      API.get("/admin/settings"),
      API.get("/products", { params: { featured: true } }),
      API.get("/testimonials"),
      API.get("/gallery"),
    ]).then(([c, s, p, t, g]) => {
      const doc = c.data || {};
      setContent({
        ...doc,
        styles: mergeStyles(doc.styles),
        section_order: doc.section_order?.length ? doc.section_order : DEFAULT_SECTION_ORDER,
      });
      setSettings(s.data);
      setData((d) => ({ ...d, settings: s.data, featured: p.data.slice(0, 4), testimonials: t.data, gallery: g.data }));
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    setData((d) => ({ ...d, occasionsChildren: categoryTree.find((c) => c.slug === "occasions")?.children || [] }));
  }, [categoryTree]);

  if (!content || !settings) return <Skeleton className="h-96 rounded-xl" />;

  const styles = mergeStyles(content.styles);
  const hero = styles.hero;
  const branding = settings.branding || {};

  const setHero = (k, v) => setContent((c) => ({ ...c, styles: { ...mergeStyles(c.styles), hero: { ...mergeStyles(c.styles).hero, [k]: v } } }));
  const setStyle = (k, v) => setContent((c) => ({ ...c, styles: { ...mergeStyles(c.styles), [k]: v } }));
  const setField = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const setBranding = (k, v) => setSettings((s) => ({ ...s, branding: { ...(s.branding || {}), [k]: v } }));

  const moveSection = (key, dir) => {
    const order = [...content.section_order];
    const i = order.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    setField("section_order", order);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...content };
      delete payload.key;
      delete payload._id;
      await API.put("/admin/content/home", payload);
      await API.put("/admin/settings", settings);
      await refresh();
      toast.success("Landing page published");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const flagKey = SHOW_FLAG[selected];

  return (
    <div data-testid="landing-editor-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-3xl tracking-tight flex items-center gap-2"><Paintbrush className="h-6 w-6 text-primary" /> Landing Page Editor</h1>
          <p className="text-sm text-muted-foreground mt-1">Click a section below to edit its text, fonts, colours and size. Reorder or hide sections, then publish.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={mobilePreview ? "default" : "outline"}
            className="rounded-full"
            onClick={() => setMobilePreview((v) => !v)}
            data-testid="editor-mobile-preview-btn"
          >
            <Smartphone className="mr-2 h-4 w-4" /> {mobilePreview ? "Mobile preview" : "Preview mobile"}
          </Button>
          <Button variant="outline" asChild className="rounded-full" data-testid="editor-view-live-btn">
            <a href="/" target="_blank" rel="noreferrer">View live page</a>
          </Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="editor-publish-btn">
            <Save className="mr-2 h-4 w-4" /> {saving ? "Publishing…" : "Publish changes"}
          </Button>
        </div>
      </div>

      <div className="grid xl:grid-cols-[1fr_340px] gap-6 items-start">
        <div className="border rounded-xl overflow-hidden bg-background shadow-sm" data-testid="editor-preview">
          <div className="bg-muted/60 border-b px-4 py-2 text-xs text-muted-foreground flex items-center gap-2">
            <Eye className="h-3.5 w-3.5" /> Live preview — click any section to edit it
          </div>
          <div className="max-h-[70vh] overflow-y-auto" style={mobilePreview ? { maxWidth: 400, margin: "0 auto" } : {}}>
            <HomeSections content={content} data={data} editable selected={selected} onSelect={setSelected} onHeroResize={(w) => setHero("panel_width", w)} forceMobilePanel={mobilePreview} />
          </div>
        </div>

        <div className="space-y-4">
          <Card data-testid="editor-sections-card">
            <CardContent className="p-4">
              <p className="text-sm font-semibold mb-3">Sections — reorder / hide</p>
              <div className="space-y-1.5">
                {["hero", ...content.section_order].map((key, i, arr) => (
                  <div
                    key={key}
                    onClick={() => setSelected(key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${selected === key ? "border-primary bg-primary/5" : "hover:border-primary/40"}`}
                    data-testid={`editor-section-row-${key}`}
                  >
                    <span className="flex-1 font-medium">{SECTION_LABELS[key] || key}</span>
                    {flagKey && key !== "hero" && SHOW_FLAG[key] && (
                      <Switch
                        checked={content[SHOW_FLAG[key]] !== false}
                        onCheckedChange={(v) => setField(SHOW_FLAG[key], v)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`editor-show-${key}`}
                        aria-label="Show section"
                      />
                    )}
                    {key !== "hero" && (
                      <span className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i <= 1} onClick={() => moveSection(key, -1)} data-testid={`editor-move-up-${key}`} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === arr.length - 1} onClick={() => moveSection(key, 1)} data-testid={`editor-move-down-${key}`} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></Button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="editor-controls-card">
            <CardContent className="p-4 space-y-4">
              <p className="text-sm font-semibold">Editing: <Badge className="rounded-full">{SECTION_LABELS[selected] || selected}</Badge></p>

              {selected === "hero" && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Heading text</Label>
                    <Textarea rows={2} value={content.hero_heading || ""} onChange={(e) => setField("hero_heading", e.target.value)} data-testid="editor-hero-heading" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Accent line (script)</Label>
                    <Input value={content.hero_accent || ""} onChange={(e) => setField("hero_accent", e.target.value)} data-testid="editor-hero-accent" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Intro text</Label>
                    <Textarea rows={2} value={content.hero_text || ""} onChange={(e) => setField("hero_text", e.target.value)} data-testid="editor-hero-text" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Heading font</Label>
                      <Select value={hero.heading_font} onValueChange={(v) => setHero("heading_font", v)}>
                        <SelectTrigger data-testid="editor-heading-font"><SelectValue /></SelectTrigger>
                        <SelectContent>{FONT_OPTIONS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Accent font</Label>
                      <Select value={hero.accent_font} onValueChange={(v) => setHero("accent_font", v)}>
                        <SelectTrigger data-testid="editor-accent-font"><SelectValue /></SelectTrigger>
                        <SelectContent>{FONT_OPTIONS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Heading size — {hero.heading_size}px</Label>
                    <input type="range" min="32" max="76" value={hero.heading_size} onChange={(e) => setHero("heading_size", Number(e.target.value))} className="w-full accent-primary" data-testid="editor-heading-size" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Accent size — {hero.accent_size}px</Label>
                    <input type="range" min="22" max="52" value={hero.accent_size} onChange={(e) => setHero("accent_size", Number(e.target.value))} className="w-full accent-primary" data-testid="editor-accent-size" />
                  </div>
                  <ColorField label="Heading colour" value={hero.heading_color} onChange={(v) => setHero("heading_color", v)} allowClear={false} testId="editor-heading-color" />
                  <ColorField label="Accent colour" value={hero.accent_color} onChange={(v) => setHero("accent_color", v)} allowClear={false} testId="editor-accent-color" />
                  <ColorField label="Text colour" value={hero.text_color} onChange={(v) => setHero("text_color", v)} allowClear={false} testId="editor-text-color" />
                  <div className="space-y-1.5">
                    <Label className="text-xs">Image darkening — {hero.overlay}%</Label>
                    <input type="range" min="0" max="70" value={hero.overlay} onChange={(e) => setHero("overlay", Number(e.target.value))} className="w-full accent-primary" data-testid="editor-overlay" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Text box width — {hero.panel_width}% (or drag the handle on the box)</Label>
                    <input type="range" min="30" max="100" value={hero.panel_width} onChange={(e) => setHero("panel_width", Number(e.target.value))} className="w-full accent-primary" data-testid="editor-panel-width" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Text box padding — {hero.panel_padding}px</Label>
                    <input type="range" min="12" max="72" value={hero.panel_padding} onChange={(e) => setHero("panel_padding", Number(e.target.value))} className="w-full accent-primary" data-testid="editor-panel-padding" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Light panel behind text</Label>
                    <Switch checked={!!hero.panel} onCheckedChange={(v) => setHero("panel", v)} data-testid="editor-panel-switch" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Mobile box transparency — {hero.panel_opacity ?? 85}% opaque</Label>
                    <input type="range" min="10" max="100" value={hero.panel_opacity ?? 85} onChange={(e) => setHero("panel_opacity", Number(e.target.value))} className="w-full accent-primary" data-testid="editor-panel-opacity" />
                    <p className="text-[11px] text-muted-foreground">Mobile only — the box never appears on desktop.</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Text alignment</Label>
                    <div className="flex gap-1">
                      <Button variant={hero.align === "left" ? "default" : "outline"} size="icon" className="h-8 w-8" onClick={() => setHero("align", "left")} data-testid="editor-align-left" aria-label="Align left"><AlignLeft className="h-4 w-4" /></Button>
                      <Button variant={hero.align === "center" ? "default" : "outline"} size="icon" className="h-8 w-8" onClick={() => setHero("align", "center")} data-testid="editor-align-center" aria-label="Align center"><AlignCenter className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </>
              )}

              {["occasions", "featured", "testimonials", "gallery"].includes(selected) && (
                <ColorField label="Section title colour" value={styles[`${selected === "occasions" ? "occasions" : selected}_title_color`] || ""} onChange={(v) => setStyle(`${selected}_title_color`, v)} testId="editor-title-color" />
              )}
              {selected === "features" && <ColorField label="Band background colour" value={styles.features_bg} onChange={(v) => setStyle("features_bg", v)} testId="editor-features-bg" />}
              {selected === "promo" && <ColorField label="Band background colour" value={styles.promo_bg} onChange={(v) => setStyle("promo_bg", v)} testId="editor-promo-bg" />}
              {selected === "accreditation" && <p className="text-xs text-muted-foreground">The badge image itself is managed under Branding & Logo.</p>}
            </CardContent>
          </Card>

          <Card data-testid="editor-theme-card">
            <CardContent className="p-4 space-y-4">
              <p className="text-sm font-semibold">Colour theme (whole site)</p>
              <ColorField label="Primary colour (buttons, highlights)" value={branding.accent_color || ""} onChange={(v) => setBranding("accent_color", v)} testId="editor-theme-primary" />
              <ColorField label="Secondary colour (soft bands)" value={branding.secondary_color || ""} onChange={(v) => setBranding("secondary_color", v)} testId="editor-theme-secondary" />
              <p className="text-xs text-muted-foreground flex items-center gap-1"><EyeOff className="h-3 w-3" /> Leave blank for the default sage & cream theme.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
